// File from v2: https://github.com/0xProject/matcha-frontend/blob/master/utils/fees.tsx
import Decimal from 'decimal.js-light';
import { Address, formatUnits, parseEther } from 'viem';

import { matchaFees, MatchaFeeType } from '@/constants/matchaFees';

import { CHAIN_IDS } from '../constants/chain';
import { getLimitOrderFeeBips } from './fee-data';
import type { EthereumAddress } from './models';
import { isTokenAddressNativeAsset } from './multichain';

export const LIMIT_ORDER_FEE_RECIPIENT_ADDRESS = '0x9b858Be6E3047D88820f439B240deaC2418a2551';

export const FEE_BUFFER = 1.2;

/**
 * Get limit order fee amount
 * @param makerToken - maker token address
 * @param takerToken - taker token address
 * @param takerAmount - string token amount (currency amount is formatted to token decimals)
 * @param chainId - chain id
 */
export const getLimitOrderFeesAmountInUnits = async (
  makerToken: Address,
  takerToken: Address,
  takerAmount: string,
  chainId: number,
): Promise<bigint> => {
  const maybeFeeForPair = await getLimitOrderFeeBips(makerToken, takerToken, chainId);

  if (!maybeFeeForPair) {
    return 0n;
  }

  const cutInBips = maybeFeeForPair;
  const feeAmount = new Decimal(takerAmount)
    .mul(cutInBips)
    .mul(0.0001) // Convert bips denominator to value
    .toInteger(); // Floor the number as we can't have any decimals

  const FEE_THRESHOLD_AMOUNT = 1; // Note: We shouldn't charge fees on small enough orders imo, let's figure that out. (johnrjj)
  if (feeAmount.lessThanOrEqualTo(FEE_THRESHOLD_AMOUNT)) {
    return 0n;
  }

  return BigInt(feeAmount.toFixed());
};

/**
 * Cross-chain gas fee buffer. These gas estimates are based on Bungee.
 * For example, visit https://bungee.exchange and set the new chain as
 * the origin chain. If you have 1 ETH and sell your maximum amount,
 * Bungee might only allow you to sell 0.97 ETH, reserving 0.03 ETH
 * for gas fees.
 */
export const crossChainGasEstimates: Record<string, number> = {
  [CHAIN_IDS.MAINNET]: 0.03, // On Ethereum reserve 0.03 ETH for gas
  [CHAIN_IDS.OPTIMISM]: 0.002, // On Optimism reserve 0.002 ETH for gas
  [CHAIN_IDS.BSC]: 0.003, // On BNB Chain reserve 0.003 BNB for gas
  [CHAIN_IDS.MATIC]: 0.06, // On Polygon reserve 0.06 MATIC for gas
  [CHAIN_IDS.BASE]: 0.002, // On Base reserve 0.002 ETH for gas
  [CHAIN_IDS.UNICHAIN]: 0.002, // On Unichain reserve 0.002 ETH for gas
  [CHAIN_IDS.ARBITRUM]: 0.003, // On Arbitrum reserve 0.003 ETH for gas
  [CHAIN_IDS.AVALANCHE]: 0.042, // On Avalanche reserve 0.042 AVAX for gas
  [CHAIN_IDS.SCROLL]: 0.001, // On Scroll reserve 0.001 ETH for gas
  [CHAIN_IDS.LINEA]: 0.001, // On Linea reserve 0.001 ETH for gas
  [CHAIN_IDS.MANTLE]: 0.001, // On Mantle reserve 0.001 MNT for gas
  [CHAIN_IDS.PLASMA]: 0.1, // On Plasma reserve 0.1 XPL for gas
  [CHAIN_IDS.MONAD]: 0.3, // On Monad reserve 0.3 MON for gas
  [CHAIN_IDS.HYPEREVM]: 0.01, // On HyperEVM reserve 0.1 HYPE for gas
  [CHAIN_IDS.ROBINHOOD]: 0.002, // On Robinhood Chain reserve 0.002 ETH for gas
  [CHAIN_IDS.INK]: 0.002, // On Ink reserve 0.002 ETH for gas
  [CHAIN_IDS.ARC]: 0.02, // On Arc reserve 0.02 USDC for gas
};

/**
 * Multiplier applied to the quote's `totalNetworkFee` when reserving native
 * funds for a swap. Wallets price transactions independently at submission
 * time (often picking a higher tier than the quote was priced at) and on
 * some chains pad the gas limit on top of the API estimate, so the
 * reservation has to absorb both gaps. Higher values fit chains with
 * volatile priority-fee markets (Linea) or where the wallet's gas-limit
 * padding directly inflates cost (Monad charges the gas limit, not gas
 * used).
 */
export const NATIVE_FEE_RESERVE_DEFAULT_MULTIPLIER = 2.0;
export const NATIVE_FEE_RESERVE_MULTIPLIER: Record<number, number> = {
  [CHAIN_IDS.LINEA]: 3.0,
  [CHAIN_IDS.MONAD]: 2.5,
};

/**
 * Defensive lower bounds for the native fee reserve on same-chain swaps.
 * Acts as a safety net for chains where the quote's `totalNetworkFee` can
 * be much smaller than what wallets actually deduct — e.g. Plasma's
 * sub-gwei gas prices vs wallet minimum-gas-price floors, or Monad's
 * `gas charged = gas limit` model when the wallet pads the limit.
 */
const sameChainNativeFeeFloorsEther: Record<number, number> = {
  [CHAIN_IDS.MAINNET]: 0.0005,
  [CHAIN_IDS.OPTIMISM]: 0.00005,
  [CHAIN_IDS.BSC]: 0.0001,
  [CHAIN_IDS.MATIC]: 0.001,
  [CHAIN_IDS.BASE]: 0.00005,
  [CHAIN_IDS.UNICHAIN]: 0.00005,
  [CHAIN_IDS.ARBITRUM]: 0.0001,
  [CHAIN_IDS.AVALANCHE]: 0.002,
  [CHAIN_IDS.SCROLL]: 0.0001,
  [CHAIN_IDS.LINEA]: 0.0002,
  [CHAIN_IDS.MANTLE]: 0.0001,
  [CHAIN_IDS.MONAD]: 0.05,
  [CHAIN_IDS.PLASMA]: 0.005,
  [CHAIN_IDS.HYPEREVM]: 0.01,
};

/**
 * Compute a worst-case native fee reserve in wei for a swap.
 *
 * Uses 0x's `totalNetworkFee` as the basis — it already includes L1 data
 * costs on optimistic rollups (Base, Optimism, Unichain, Scroll, Mantle)
 * per their API spec — then scales it by a per-chain multiplier that
 * absorbs (a) base-fee growth between quote and submission, (b) wallets
 * bidding above the quote's priority-fee tier, and (c) gas-limit padding
 * by the wallet. A per-chain static floor backs this up for chains whose
 * `totalNetworkFee` can be unreasonably small relative to wallet behavior.
 */
export function computeNativeFeeReserveWei(totalNetworkFeeWei: bigint, chainId: number): bigint {
  const multiplier =
    NATIVE_FEE_RESERVE_MULTIPLIER[chainId] ?? NATIVE_FEE_RESERVE_DEFAULT_MULTIPLIER;

  const reserveWei = BigInt(
    new Decimal(totalNetworkFeeWei.toString()).mul(multiplier).toFixed(0, Decimal.ROUND_DOWN),
  );

  const floorEther = sameChainNativeFeeFloorsEther[chainId];
  if (floorEther === undefined) return reserveWei;
  const floorWei = parseEther(floorEther.toString());
  return reserveWei > floorWei ? reserveWei : floorWei;
}

export function formatMatchaFeeRange(type: MatchaFeeType): string {
  const { stables, default: defaultFees } = matchaFees[type];
  const hasFees = stables > 0 || defaultFees > 0;

  return !hasFees ? '0%' : `${stables / 100}% - ${defaultFees / 100}%`;
}

/**
 * Check whether the native token balance is enough to cover fees.
 * In the case of selling a native token, the required balance includes the sell amount + fees.
 *
 * Computes the reserve via `computeNativeFeeReserveWei`, which scales the
 * quote's `totalNetworkFee` (`networkFeesInEthers`) by a per-chain
 * multiplier and applies a static floor.
 */
export function getHasEnoughBalanceForAmountPlusFees({
  chainId,
  nativeTokenBalance,
  networkFeesInEthers,
  sellAmount,
  sellTokenAddress,
  sellTokenDecimals,
}: {
  /** Chain Id for the trade */
  chainId: number;
  /** User's native token balance on `chainId` chain */
  nativeTokenBalance: string;
  /** Quote's `totalNetworkFee` expressed in native-token ether units */
  networkFeesInEthers: number;
  /** Current sell amount */
  sellAmount: string;
  /** Sell token address */
  sellTokenAddress: EthereumAddress;
  /** Sell token decimal units */
  sellTokenDecimals: number;
}) {
  const isSellTokenNative = isTokenAddressNativeAsset(sellTokenAddress, chainId);
  const sellAmountInEtherUnits = new Decimal(formatUnits(BigInt(sellAmount), sellTokenDecimals));

  const totalNetworkFeeWei = parseEther(
    new Decimal(networkFeesInEthers).toFixed(18, Decimal.ROUND_DOWN),
  );
  const reserveWei = computeNativeFeeReserveWei(totalNetworkFeeWei, chainId);
  const reserveInEther = new Decimal(formatUnits(reserveWei, 18));
  const requiredBalance = reserveInEther.plus(isSellTokenNative ? sellAmountInEtherUnits : 0);
  return requiredBalance.lessThanOrEqualTo(nativeTokenBalance);
}
